import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useToolboxAttendanceAnalytics } from '@/hooks/use-safety';
import { useOrgTimeZone } from '@/hooks/use-settings';
import {
  formatLocalDate,
  todayInTimeZone,
} from '@/lib/date-utils';
import { parseISO, subYears } from 'date-fns';

function formatPercent(value: number) {
  return `${(value * 100).toFixed(0)}%`;
}

export function ToolboxAnalyticsSection() {
  const timeZone = useOrgTimeZone();
  const orgToday = todayInTimeZone(timeZone);
  const defaultDateFrom = formatLocalDate(subYears(parseISO(orgToday), 1));
  const [dateRange, setDateRange] = useState<{
    dateFrom: string;
    dateTo: string;
  } | null>(null);
  const dateFrom = dateRange?.dateFrom ?? defaultDateFrom;
  const dateTo = dateRange?.dateTo ?? orgToday;

  const { data: analytics, isLoading } = useToolboxAttendanceAnalytics({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Toolbox Analytics</h2>
          <p className="text-sm text-gray-600">Attendance and kit-inspection summary</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="toolbox-date-from" className="text-sm whitespace-nowrap">
              From
            </Label>
            <Input
              id="toolbox-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) =>
                setDateRange((current) => ({
                  dateFrom: e.target.value,
                  dateTo: current?.dateTo ?? dateTo,
                }))
              }
              className="w-auto"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="toolbox-date-to" className="text-sm whitespace-nowrap">
              To
            </Label>
            <Input
              id="toolbox-date-to"
              type="date"
              value={dateTo}
              onChange={(e) =>
                setDateRange((current) => ({
                  dateFrom: current?.dateFrom ?? dateFrom,
                  dateTo: e.target.value,
                }))
              }
              className="w-auto"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Talks</p>
            <p className="text-2xl font-bold mt-1">{analytics.total_talks}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Sign-ins</p>
            <p className="text-2xl font-bold mt-1">{analytics.total_signins}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Kit Inspection Rate</p>
            <p className="text-2xl font-bold mt-1">
              {formatPercent(analytics.kit_inspection_rate)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Attendance by Person</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Attended</TableHead>
                <TableHead>Total Talks</TableHead>
                <TableHead>Attendance Rate</TableHead>
                <TableHead>Kit Inspections</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics.per_person.map((row) => (
                <TableRow key={row.tech_id}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell>{row.attended}</TableCell>
                  <TableCell>{row.total_talks}</TableCell>
                  <TableCell>{formatPercent(row.attendance_rate)}</TableCell>
                  <TableCell>{row.kit_inspections}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Completion by Template</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Occurrences Held</TableHead>
                <TableHead>Completion Rate</TableHead>
                <TableHead>Avg. Attendance / Occurrence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics.per_template.map((row) => (
                <TableRow key={row.template_id}>
                  <TableCell className="font-medium">{row.title}</TableCell>
                  <TableCell>{row.occurrences_held}</TableCell>
                  <TableCell>{formatPercent(row.completion_rate)}</TableCell>
                  <TableCell>{row.avg_attendance_per_occurrence.toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
